
import React from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { ReagentRequest } from "@/types/reagentRequest.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Trash, Edit, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { Link, useNavigate } from "react-router-dom";
import { deleteReagentRequest } from "@/services/reagentRequests";

interface ReagentRequestCardProps {
  request: ReagentRequest;
  onUpdate: () => void;
  onEdit?: (request: ReagentRequest) => void;
}

const ReagentRequestCard: React.FC<ReagentRequestCardProps> = ({ request, onUpdate, onEdit }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.id === request.user_id;
  
  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this request?")) {
      const success = await deleteReagentRequest(request.id);
      if (success) {
        onUpdate();
      }
    }
  };
  
  const handleContact = () => {
    navigate(`/conversations/${request.user_id}`, { state: { requestId: request.id } });
  };
  
  const handleEdit = () => {
    if (onEdit && isOwner) {
      onEdit(request);
    }
  };
  
  const renderLocation = () => {
    if (request.city || request.country) {
      return (
        <div className="flex items-center text-sm text-gray-500 mt-2">
          <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">
            {[request.city, request.country].filter(Boolean).join(", ")}
          </span>
        </div>
      );
    }
    return null;
  };
  
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{request.name}</CardTitle>
          {request.categories && (
            <Badge variant="outline">{request.categories.name}</Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow">
        {request.catalogue_number && (
          <p className="text-sm text-muted-foreground mb-2">
            Catalogue: {request.catalogue_number}
          </p>
        )}
        
        {request.description && (
          <p className="text-sm mb-4">{request.description}</p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {request.amount && request.amount_unit && (
            <div>
              <span className="font-medium">Amount: </span>
              {request.amount} {request.amount_unit}
            </div>
          )}
          
          {request.number_of_packages && (
            <div>
              <span className="font-medium">Packages: </span>
              {request.number_of_packages}
            </div>
          )}
          
          {request.storage_conditions && (
            <div className="col-span-2">
              <span className="font-medium">Storage: </span>
              {request.storage_conditions}
            </div>
          )}
        </div>
        
        {renderLocation()}
        
        <div className="mt-4 text-sm">
          <p>
            Requested by{" "}
            <Link 
              to={`/community/profile/${request.user_id}`}
              className="font-medium text-primary hover:underline"
            >
              {request.profiles?.display_username || "Unknown User"}
            </Link>
          </p>
          <p className="text-muted-foreground">
            {request.profiles?.city && request.profiles?.country && (
              <span>
                {[request.profiles.city, request.profiles.country].filter(Boolean).join(", ")}
              </span>
            )}
          </p>
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between border-t pt-4">
        <p className="text-xs text-muted-foreground">
          Posted {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
        </p>
        
        <div className="flex gap-2">
          {isOwner ? (
            <>
              {onEdit && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleEdit}
                  className="flex items-center gap-1"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleDelete}
                className="flex items-center gap-1"
              >
                <Trash className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleContact}
              className="flex items-center gap-1"
            >
              <MessageSquare className="h-4 w-4" />
              Contact
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default ReagentRequestCard;
